import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Spinner} from '../../Spinner';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {ArrowRight} from '../../icons';
import {useLineWizard, wizardPath} from '../LineWizardScreen';
import {CLIENT_MODE_EXISTING, CLIENT_MODE_NEW} from '../lineWizardDraft';
import {fetchAgencyData} from '../lineWizardApi';

/**
 * Paso 1 · Cliente — «Usar un cliente existente» o «Crear un cliente nuevo».
 *
 * El cliente nuevo NO se crea acá: se crea recién al pasar del paso 3 al 4, junto con la línea. Así,
 * quien empieza el wizard y lo abandona no deja un cliente vacío en su listado.
 *
 * El caso «el cliente existe pero no está asignado al usuario» no se resuelve adentro del wizard
 * (mockup, nota de `#/mano`): la asignación vive en el listado de clientes.
 */
export function StepClient() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const {draft, update} = useLineWizard();
    const [clients, setClients] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [showError, setShowError] = useState(false);

    const load = () => {
        setLoadError(null);
        setClients(null);
        fetchAgencyData().then(response => {
            if (response?.status !== 'OK') {
                setLoadError('line_wizard.load_error');
                return;
            }
            const active = (response.clients || []).filter(client => client.active !== false);
            active.sort((a, b) => String(a.name).localeCompare(String(b.name)));
            setClients(active);
        });
    };

    useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

    const {mode, id, newName} = draft.client;
    const locked = !!draft.created.clientId;
    const valid = mode === CLIENT_MODE_EXISTING ? !!id : newName.trim().length > 0;

    const setClient = (changes) => update(current => ({...current, client: {...current.client, ...changes}}));

    const next = () => {
        if (!valid) {
            setShowError(true);
            return;
        }
        navigate(wizardPath('linea'));
    };

    if (loadError !== null) {
        return (
            <div className="lw-step">
                <LoadErrorState title={t(loadError)} text={t('line_wizard.load_error_explanation')}
                                retryLabel={t('line_wizard.load_error_retry')} onRetry={load}/>
            </div>
        );
    }
    if (clients === null) return <div className="box-loader"><Spinner color="positive"/></div>;

    return (
        <div className="lw-step">
            <div className="ob-head">
                <h1>{t('line_wizard.client.title')}</h1>
                <p>{t('line_wizard.client.subtitle')}</p>
            </div>

            <div className="lw-modes" role="radiogroup">
                {[CLIENT_MODE_EXISTING, CLIENT_MODE_NEW].map(option => (
                    <button key={option} type="button" role="radio" aria-checked={mode === option}
                            disabled={locked}
                            className={`lw-mode${mode === option ? ' -on' : ''}`}
                            onClick={() => setClient({mode: option})}>
                        <span className="lw-mode__radio"/>
                        <span className="lw-mode__title">
                            {option === CLIENT_MODE_EXISTING ? t('line_wizard.client.existing') : t('line_wizard.client.new')}
                        </span>
                    </button>
                ))}
            </div>

            <div className="lw-card">
                {mode === CLIENT_MODE_EXISTING ? (
                    <div className="lw-field">
                        <label htmlFor="lw-client">{t('line_wizard.client.client_label')}</label>
                        <select id="lw-client" value={id || ''} disabled={locked}
                                onChange={event => {
                                    const chosen = clients.find(client => String(client.id) === event.target.value);
                                    setClient({id: event.target.value || null, name: chosen ? chosen.name : ''});
                                }}>
                            <option value="">{t('line_wizard.client.client_placeholder')}</option>
                            {clients.map(client => (
                                <option key={client.id} value={String(client.id)}>{client.name}</option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <div className="lw-field">
                        <label htmlFor="lw-client-name">{t('line_wizard.client.new_name_label')}</label>
                        <input id="lw-client-name" type="text" value={newName} disabled={locked}
                               placeholder={t('line_wizard.client.new_name_placeholder')}
                               onChange={event => setClient({newName: event.target.value})}/>
                        <p className="lw-help">{t('line_wizard.client.new_name_help')}</p>
                    </div>
                )}
                {showError && !valid && <p className="lw-error">{t('line_wizard.client.missing')}</p>}
            </div>

            <div className="lw-foot">
                <span className="lw-foot__spacer"/>
                <button type="button" className="ob-btn -primary" onClick={next}>
                    {t('line_wizard.next')} <ArrowRight size={16}/>
                </button>
            </div>
        </div>
    );
}
